/**
 * [HF clock-on-board] The standalone snake board's pick clock.
 *
 * Split out of SnakeStandaloneDraftBoard so it is directly renderable in a
 * test: the board module pulls in lucide-react + axios + four contexts, which
 * the current jest transform cannot load. Behaviour is unchanged.
 *
 * Carries two shipped behaviours:
 *   • [BUG-C] the on-the-clock TEAM NAME beside the digits
 *   • [SNAKE TIMER REV] an untimed league says so instead of freezing at 00:00
 */
import { useEffect, useState } from "react";
import { isUntimed, UNTIMED_LABEL } from "../../utils/pickTimer";

// [HF clock-on-board] Snake board persistent timer. Resets on every
// `timer_version` bump (engine signal that the timer was started or
// restarted). Subscribes to `draftState.timer_remaining` for the initial
// duration. Mirrors the auction StandaloneDraftBoard's `useTimer` pattern
// + the SnakeCenterSurface `useCountdown` so every snake surface
// (action panel + board) ticks in lockstep with the WS broadcast.
function useSnakeBoardTimer(initial, version) {
  const [t, setT] = useState(initial || 0);
  useEffect(() => {
    setT(initial || 0);
  }, [initial, version]);
  useEffect(() => {
    if (!initial || initial <= 0) return undefined;
    const id = setInterval(() => {
      setT((x) => (x > 0 ? x - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [initial, version]);
  return t;
}

// [HF clock-on-board] Persistent on-the-clock timer for the snake board.
// Renders during DRAFTING only - between-rounds + DRAFT_COMPLETE carry
// their own surfaces (BetweenRoundsCountdownModal, DraftEndRecapModal)
// that already include their own clocks. Pulse + red escalate in last 5s.
export default function SnakeBoardTimer({ draftState, isDark }) {
  const isDrafting = draftState?.state === "DRAFTING";
  const timeLeft = useSnakeBoardTimer(
    draftState?.timer_remaining,
    draftState?.timer_version,
  );
  if (!isDrafting) return null;
  // [SNAKE TIMER REV] Untimed leagues never start a clock, so the digits would
  // sit frozen at 00:00. Show the state instead.
  const untimed = isUntimed(draftState);
  const mins = String(Math.floor(Math.max(0, timeLeft) / 60)).padStart(2, "0");
  const secs = String(Math.max(0, timeLeft) % 60).padStart(2, "0");
  const isLowTime = !untimed && timeLeft <= 5 && timeLeft > 0;
  const color = isLowTime ? "#E03030" : (isDark ? "#F5F5F5" : "#0A0A0A");
  const subColor = isDark ? "rgba(245,245,245,0.55)" : "rgba(10,10,10,0.55)";
  const bg = isDark ? "rgba(255,107,0,0.10)" : "rgba(255,107,0,0.12)";
  const border = isDark ? "rgba(255,107,0,0.35)" : "rgba(255,107,0,0.40)";
  // [BUG-C] The clock showed the time but never WHO. Team name sits beside
  // the digits in sportscast display caps.
  const pickerId = draftState?.current_picker_id;
  const onClockName = pickerId
    ? (draftState?.managers?.[pickerId]?.team_name || "").trim()
    : "";
  return (
    <div
      data-testid="snake-board-timer"
      className={`flex items-center justify-center gap-4 rounded-xl px-5 py-2 ${isLowTime ? "animate-pulse" : ""}`}
      style={{ background: bg, border: `1px solid ${border}`, minWidth: 132 }}
    >
      <div className="flex flex-col items-center justify-center">
        {untimed ? (
          <p
            className="font-display font-black uppercase leading-none tracking-tight"
            style={{ color, fontSize: 20 }}
            data-testid="snake-board-untimed"
          >
            {UNTIMED_LABEL}
          </p>
        ) : (
          <>
            <p
              className="font-mono font-black tabular-nums leading-none"
              style={{ color, fontSize: 40 }}
              data-testid="snake-board-timer-value"
            >
              {mins}:{secs}
            </p>
            <p
              className="font-display font-bold uppercase tracking-[0.3em] mt-1"
              style={{ color: subColor, fontSize: 9 }}
            >
              On the Clock
            </p>
          </>
        )}
      </div>
      {onClockName ? (
        <p
          className="font-display font-black uppercase leading-none tracking-tight"
          style={{
            color: isDark ? "#F5F5F5" : "#0A0A0A",
            fontSize: 30,
            maxWidth: 320,
            borderLeft: `1px solid ${border}`,
            paddingLeft: 16,
          }}
          data-testid="snake-board-on-clock-team"
        >
          {onClockName}
        </p>
      ) : null}
    </div>
  );
}
